import { MouseEvent, useMemo, useState, useCallback, useEffect } from "react"
import { useLocation } from "react-router-dom"
import { useStableNavigate } from "~/ui-rtk/utils/StableNavigateContext"

import type { NavItem } from "../types"

export const useConnect = (
  path?: string,
  onBeforeNavigate?: () => boolean,
  onNavigate?: () => void,
  items?: NavItem[],
  labelVisible?: boolean,
) => {
  const navigate = useStableNavigate()
  const location = useLocation()
  const isActive = useMemo(() => {
    if (!path) {
      return false
    }

    const [itemPathname, itemQuery] = path.split("?")

    // Query-carrying entries (e.g. the free-SoS "Keywords & Competitors"
    // deep link, APP-493) are transient actions rather than pages: only
    // highlight them while their query params are actually in the URL.
    // Without this, such an entry would match its sibling page entry's
    // pathname and both would highlight permanently.
    if (itemQuery) {
      if (location.pathname !== itemPathname) {
        return false
      }
      const currentParams = new URLSearchParams(location.search)
      return [...new URLSearchParams(itemQuery).entries()].every(
        ([key, value]) => currentParams.get(key) === value,
      )
    }

    if (location.pathname === path) {
      return true
    }

    const prefix = location.pathname.split("/")[1]
    return path.startsWith(`/${prefix}`)
  }, [path, location])

  // Check if any child item is active
  const hasActiveChild = useMemo(() => {
    if (!items?.length) return false
    return items.some(item => {
      if (location.pathname === item.path) {
        return true
      }
      const prefix = location.pathname.split("/")[1]
      return item.path.startsWith(`/${prefix}`)
    })
  }, [items, location])

  // Default to collapsed, but expand if this item or any child is active
  const [isExpanded, setIsExpanded] = useState(() => isActive || hasActiveChild)

  // Update expanded state when route changes: expand if active, collapse if not
  useEffect(() => {
    if (isActive || hasActiveChild) {
      setIsExpanded(true)
    } else {
      setIsExpanded(false)
    }
  }, [isActive, hasActiveChild, location.pathname])

  const handleClick = useCallback(
    (event: MouseEvent) => {
      event.preventDefault()
      event.stopPropagation()

      if (items?.length && onBeforeNavigate && !onBeforeNavigate()) {
        setIsExpanded(true)
        return
      }

      if (items?.length) {
        setIsExpanded(prev => !prev)
        return
      }

      if (path) {
        navigate(path)
      }

      onNavigate?.()
    },
    [path, onBeforeNavigate, onNavigate, labelVisible, items, setIsExpanded],
  )

  const handleNavigate = useCallback(() => {
    onNavigate?.()
  }, [setIsExpanded, onNavigate])

  return {
    isActive,
    isExpanded,
    handleClick,
    handleNavigate,
  }
}
